import { formatCodeableConcept } from "@medplum/core";
import { Observation, ObservationComponent, Quantity } from "@medplum/fhirtypes";

import isEmpty from "lodash/isEmpty";

import {
  ClientFirstLatchComponent,
  ClientInitialIppAssessmentComponent,
  ClientInputOutputComponent,
  ClientIPPVitalsComponent,
  ClientPerineumComponent,
  ClientPlacentaComponent,
  ClientPositionComponent,
  CommonComponent,
  FORM_OPTIONS,
  NewbornApgarComponent,
  NewbornFirstLatchComponent,
  NewbornPostpartumFlowsheetForms,
  NewbornVitalsComponent,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import { TEMPERATURE_UNIT_DISPLAY } from "@/encounters/components/EncounterLabor/utils/labor";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { PostpartumHemorrhageComponent } from "@/fhir/enums/quilted-health";
import { getCodeableConceptCode, getCodeableConceptDisplay } from "@/utils/medplum";

// Types for row values
export interface BaseRowValues {
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
}

export interface IPPVitalsRowValues extends BaseRowValues {
  bloodPressure: string | undefined;
  pulse: string | undefined;
  temperature: string | undefined;
  fundus: string | undefined;
  fundusValue: string | undefined;
  fundalTexture: string | undefined;
  estimatedBloodLoss: string | undefined;
  quantitativeBloodLoss: string | undefined;
}

export interface PlacentaRowValues extends BaseRowValues {
  deliveryMethod: string | undefined;
  presentation: string | undefined;
  threeVesselCord: string | undefined;
  intact: string | undefined;
  appearance: string | undefined;
}

export interface InputOutputRowValues extends BaseRowValues {
  input: string | undefined;
  output: string | undefined;
}

export interface PerineumRowValues extends BaseRowValues {
  status: string | undefined;
  otherLaceration: string | undefined;
  repair: string | undefined;
}

export interface InitialIppAssessmentRowValues extends BaseRowValues {
  alert: string | undefined;
  management: string | undefined;
  bleeding: string | undefined;
}

export interface FirstLatchRowValues extends BaseRowValues {
  nursing: string | undefined;
  formula: string | undefined;
}

export interface CordClampedRowValues extends BaseRowValues {
  notes: string | undefined;
}

export interface AssessmentNoteRowValues extends BaseRowValues {
  notes: string | undefined;
}

export interface NewbornFeedingRowValues extends BaseRowValues {
  feedingTypes: string[];
}

export interface NewbornInitialAssessmentRowValues extends BaseRowValues {
  assessmentTypes: string[];
}

export interface ApgarRowValues extends BaseRowValues {
  type: { display: string; valueInteger?: number } | undefined;
  typeOther: string | undefined;
  heartRate: { display: string; valueInteger?: number } | undefined;
  respiratoryEffort: { display: string; valueInteger?: number } | undefined;
  reflexStimuli: { display: string; valueInteger?: number } | undefined;
  muscleTone: { display: string; valueInteger?: number } | undefined;
  color: { display: string; valueInteger?: number } | undefined;
  totalScore: number | undefined;
}

// Shared utility functions
function getComponentValueQuantity(
  observation: Observation | undefined,
  code: string,
): Quantity | undefined {
  const component = observation?.component?.find((comp) => {
    const compCode = getCodeableConceptCode(comp.code);
    return compCode === code;
  });
  return component?.valueQuantity;
}

function getComponentValueCodeableConcept(
  observation: Observation | undefined,
  code: string,
): { coding?: Array<{ code?: string; display?: string }> } | undefined {
  const component = observation?.component?.find((comp) => {
    const compCode = getCodeableConceptCode(comp.code);
    return compCode === code;
  });
  return component?.valueCodeableConcept;
}

function getComponentValueString(
  observation: Observation | undefined,
  code: string,
): string | undefined {
  const component = observation?.component?.find((comp) => {
    const compCode = getCodeableConceptCode(comp.code);
    return compCode === code;
  });
  return component?.valueString;
}

function getNotesDisplay(observation: Observation | undefined): string | undefined {
  return observation?.note?.[0]?.text;
}

function hasRecordsAnswers(
  answers: Record<string, string | number | boolean | undefined>,
  excludeKeys: string[],
): boolean {
  return Object.keys(answers).some((key) => {
    const value = answers[key as keyof typeof answers];
    return value !== undefined && value !== "" && !excludeKeys.includes(key);
  });
}

// IPP Vitals specific functions
function getBloodPressureDisplay(observation: Observation | undefined): string | undefined {
  const systolic = getComponentValueQuantity(observation, LOINCCodes.SystolicBloodPressure)?.value;
  const diastolic = getComponentValueQuantity(
    observation,
    LOINCCodes.DiastolicBloodPressure,
  )?.value;

  if (!systolic && !diastolic) return;
  return `${systolic}/${diastolic}`;
}

function getPulseDisplay(observation: Observation | undefined): string | undefined {
  const pulse = getComponentValueQuantity(observation, LOINCCodes.HeartRate)?.value;
  return pulse?.toString();
}

function getTemperatureDisplay(observation: Observation | undefined): string | undefined {
  const temperatureComponent = getComponentValueQuantity(observation, LOINCCodes.BodyTemperature);

  if (!temperatureComponent) return;

  const temperature = temperatureComponent.value;
  if (temperature === undefined) return;

  const temperatureUnitCode = temperatureComponent.code ?? FORM_OPTIONS.Fahrenheit;
  const temperatureUnit =
    TEMPERATURE_UNIT_DISPLAY[temperatureUnitCode as keyof typeof TEMPERATURE_UNIT_DISPLAY];

  const temperatureMethod = getCodeableConceptDisplay(observation?.method);

  if (!temperatureMethod) return `${temperature}°${temperatureUnit}`;

  return `${temperature}°${temperatureUnit} (${temperatureMethod})`;
}

function getFundusDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientIPPVitalsComponent.FundusMeasurement)
    ?.coding?.[0]?.display;
}

function getFundusValueDisplay(observation: Observation | undefined): string | undefined {
  const quantity = getComponentValueQuantity(observation, ClientIPPVitalsComponent.FundusValue);
  return quantity?.value?.toString();
}

function getFundalTextureDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientIPPVitalsComponent.FundalTexture)
    ?.coding?.[0]?.display;
}

function getEstimatedBloodLossDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueQuantity(observation, LOINCCodes.EstimatedBloodLoss)?.value?.toString();
}

function getQuantitativeBloodLossDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueQuantity(
    observation,
    LOINCCodes.QuantitativeBloodLoss,
  )?.value?.toString();
}

export function getIPPVitalsRowValues(observation: Observation | undefined): IPPVitalsRowValues {
  const answers = {
    bloodPressure: getBloodPressureDisplay(observation),
    pulse: getPulseDisplay(observation),
    temperature: getTemperatureDisplay(observation),
    fundus: getFundusDisplay(observation),
    fundusValue: getFundusValueDisplay(observation),
    fundalTexture: getFundalTextureDisplay(observation),
    estimatedBloodLoss: getEstimatedBloodLossDisplay(observation),
    quantitativeBloodLoss: getQuantitativeBloodLossDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, [CommonComponent.Notes]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

function getDeliveryMethodDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientPlacentaComponent.DeliveryMethod)
    ?.coding?.[0]?.display;
}

function getPresentationDisplay(observation: Observation | undefined): string | undefined {
  const presentation = getComponentValueCodeableConcept(
    observation,
    ClientPlacentaComponent.Presentation,
  )?.coding?.[0]?.display;
  const otherPresentation = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === ClientPlacentaComponent.PresentationOther,
  )?.valueString;

  if (presentation === FORM_OPTIONS.OTHER && otherPresentation) {
    return `${presentation} - ${otherPresentation}`;
  }
  return presentation;
}

function getThreeVesselCordDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientPlacentaComponent.ThreeVesselCord)
    ?.coding?.[0]?.display;
}

function getIntactDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientPlacentaComponent.Intact)?.coding?.[0]
    ?.display;
}

function getAppearanceDisplay(observation: Observation | undefined): string | undefined {
  const appearance = getComponentValueCodeableConcept(
    observation,
    ClientPlacentaComponent.Appearance,
  )?.coding?.[0]?.display;
  const otherAppearance = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === ClientPlacentaComponent.AppearanceOther,
  )?.valueString;

  if (appearance === FORM_OPTIONS.NOT_NORMAL && otherAppearance) {
    return `${appearance} - ${otherAppearance}`;
  }
  return appearance;
}

export function getPlacentaRowValues(observation: Observation): PlacentaRowValues {
  const answers = {
    deliveryMethod: getDeliveryMethodDisplay(observation),
    presentation: getPresentationDisplay(observation),
    threeVesselCord: getThreeVesselCordDisplay(observation),
    intact: getIntactDisplay(observation),
    appearance: getAppearanceDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, [CommonComponent.Notes]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

export function getPostpartumHemorrhageRowValues(observation: Observation | undefined): {
  duration: string | undefined;
  maneuvers: Array<{
    maneuver: string | undefined;
  }>;
  medications: Array<{
    medication: string | undefined;
    form: string | undefined;
    duration: string | undefined;
    xDays: string | undefined;
    dosage: string | undefined;
    frequency: string | undefined;
    prn: string | undefined;
    specialInstructions: string | undefined;
  }>;
  therapies: Array<{
    therapy: string | undefined;
    reason: string | undefined;
  }>;
  ivAccess: string | undefined;
  fluidVolume: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  const durationComponent = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === PostpartumHemorrhageComponent.Duration,
  );
  const duration = durationComponent?.valueTime;
  const medications = getCombinedMedications(observation);
  const therapies = getCombinedTherapies(observation);
  const ivAccess = getCombinedIVAccess(observation);
  const fluidVolume = getCombinedFluidVolume(observation);
  const notes = getNotesDisplay(observation);

  // Get maneuvers using postpartum hemorrhage specific codes
  const maneuvers =
    observation?.component
      ?.filter((comp) => {
        const code = getCodeableConceptCode(comp.code);
        return code?.startsWith(PostpartumHemorrhageComponent.Maneuvers);
      })
      .map((comp) => ({
        maneuver: comp.valueCodeableConcept?.coding?.[0]?.display || "Maneuver not specified",
      })) || [];

  const hasAnswers = Boolean(
    duration ||
      medications.length > 0 ||
      therapies.length > 0 ||
      ivAccess ||
      fluidVolume ||
      maneuvers.length > 0,
  );
  const hasNotes = !isEmpty(notes);

  return {
    duration,
    medications,
    therapies,
    maneuvers,
    ivAccess,
    fluidVolume,
    notes,
    hasAnswers,
    hasNotes,
  };
}

// Shared helper for parsing component values
interface ComponentFieldDefinition<T> {
  pattern: (prefix: string) => RegExp;
  key: keyof T;
  getValue: (component: ObservationComponent) => string | undefined;
}

function parseComponentValues<T extends Record<string, string | undefined>>(
  observation: Observation | undefined,
  fieldDefs: ComponentFieldDefinition<T>[],
  prefixes: string[],
): T[] {
  const records: T[] = [];
  const components = observation?.component as ObservationComponent[] | undefined;

  components?.forEach((component: ObservationComponent) => {
    const code = getCodeableConceptCode(component.code);
    if (!code) return;

    for (const prefix of prefixes) {
      for (const { pattern, key, getValue } of fieldDefs) {
        const regex = pattern(prefix);
        const match = code.match(regex);
        if (match) {
          const index = parseInt(match[1], 10) - 1;
          if (!records[index]) records[index] = {} as T;
          (records[index][key] as string | undefined) = getValue(component) as string | undefined;
          return;
        }
      }
    }
  });

  return records;
}

// Medication field definitions
const MEDICATION_FIELD_DEFS: ComponentFieldDefinition<{
  medication: string | undefined;
  form: string | undefined;
  duration: string | undefined;
  xDays: string | undefined;
  dosage: string | undefined;
  frequency: string | undefined;
  prn: string | undefined;
  specialInstructions: string | undefined;
}>[] = [
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-medication-(\\d+)`),
    key: "medication",
    getValue: (component: ObservationComponent) =>
      component.valueCodeableConcept?.coding?.[0]?.display ||
      component.valueString ||
      "Medication not specified",
  },
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-form-(\\d+)`),
    key: "form",
    getValue: (c: ObservationComponent) => formatCodeableConcept(c.valueCodeableConcept),
  },
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-duration-(\\d+)`),
    key: "duration",
    getValue: (c: ObservationComponent) => formatCodeableConcept(c.valueCodeableConcept),
  },
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-duration-x-days-(\\d+)`),
    key: "xDays",
    getValue: (c: ObservationComponent) => c.valueQuantity?.value?.toString(),
  },
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-dosage-(\\d+)`),
    key: "dosage",
    getValue: (c: ObservationComponent) => formatCodeableConcept(c.valueCodeableConcept),
  },
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-frequency-(\\d+)`),
    key: "frequency",
    getValue: (c: ObservationComponent) => formatCodeableConcept(c.valueCodeableConcept),
  },
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-prn-(\\d+)`),
    key: "prn",
    getValue: (c: ObservationComponent) => formatCodeableConcept(c.valueCodeableConcept),
  },
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-special-instructions-(\\d+)`),
    key: "specialInstructions",
    getValue: (c: ObservationComponent) => formatCodeableConcept(c.valueCodeableConcept),
  },
];

// Therapy field definitions
const THERAPY_FIELD_DEFS: ComponentFieldDefinition<{
  therapy: string | undefined;
  reason: string | undefined;
}>[] = [
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-therapy-(\\d+)`),
    key: "therapy",
    getValue: (c: ObservationComponent) => c.valueString || "Therapy not specified",
  },
  {
    pattern: (prefix: string) => new RegExp(`${prefix}-reason-(\\d+)`),
    key: "reason",
    getValue: (c: ObservationComponent) => c.valueCodeableConcept?.coding?.[0]?.display,
  },
];

const SHARED_PREFIXES = ["meds-therapies", "postpartum-hemorrhage"];

function getCombinedMedications(observation: Observation | undefined): Array<{
  medication: string | undefined;
  form: string | undefined;
  duration: string | undefined;
  xDays: string | undefined;
  dosage: string | undefined;
  frequency: string | undefined;
  prn: string | undefined;
  specialInstructions: string | undefined;
}> {
  return parseComponentValues(observation, MEDICATION_FIELD_DEFS, SHARED_PREFIXES);
}

function getCombinedTherapies(observation: Observation | undefined): Array<{
  therapy: string | undefined;
  reason: string | undefined;
}> {
  return parseComponentValues(observation, THERAPY_FIELD_DEFS, SHARED_PREFIXES);
}

function getCombinedIVAccess(observation: Observation | undefined): string | undefined {
  for (const prefix of SHARED_PREFIXES) {
    const component = observation?.component?.find(
      (component) => getCodeableConceptCode(component.code) === `${prefix}-iv-access`,
    );
    if (component?.valueDateTime) return component.valueDateTime;
  }
  return undefined;
}

function getCombinedFluidVolume(observation: Observation | undefined): string | undefined {
  for (const prefix of SHARED_PREFIXES) {
    const component = observation?.component?.find(
      (component) => getCodeableConceptCode(component.code) === `${prefix}-fluid-volume`,
    );
    if (component?.valueQuantity?.value !== undefined)
      return component.valueQuantity.value.toString();
  }
  return undefined;
}

export function getClientPositionRowValues(observation: Observation) {
  const notes = observation.note || [];

  const position = getComponentValueCodeableConcept(observation, ClientPositionComponent.Position)
    ?.coding?.[0]?.display;

  const inWater = getComponentValueCodeableConcept(observation, ClientPositionComponent.InWater)
    ?.coding?.[0]?.display;

  const inWaterOther = getComponentValueString(observation, ClientPositionComponent.InWaterOther);

  const notesText = notes[0]?.text;

  return {
    position,
    inWater,
    inWaterOther,
    notes: notesText,
    hasAnswers: Boolean(position || inWater),
    hasNotes: Boolean(notesText),
  };
}

export function getPostpartumMedsTherapiesRowValues(observation: Observation | undefined): {
  medications: Array<{
    medication: string | undefined;
    form: string | undefined;
    duration: string | undefined;
    xDays: string | undefined;
    dosage: string | undefined;
    frequency: string | undefined;
    prn: string | undefined;
    specialInstructions: string | undefined;
  }>;
  therapies: Array<{
    therapy: string | undefined;
    reason: string | undefined;
  }>;
  ivAccess: string | undefined;
  fluidVolume: string | undefined;
  notes: string | undefined;
  hasAnswers: boolean;
  hasNotes: boolean;
} {
  // Check if the observation is for newborn meds and therapies
  const isNewbornMedsTherapies = observation?.code?.coding?.some(
    (coding) => coding.code === NewbornPostpartumFlowsheetForms.NewbornMedsTherapies,
  );

  // Use different prefixes based on whether it's client or newborn
  const prefixes = isNewbornMedsTherapies
    ? ["newborn-meds-and-therapies"]
    : ["client-meds-therapies"];

  // Use the same pattern for both client and newborn
  const medications = parseComponentValues(observation, MEDICATION_FIELD_DEFS, prefixes);
  const therapies = parseComponentValues(observation, THERAPY_FIELD_DEFS, prefixes);

  // Filter out medications that only have "Medication not specified" with no other details
  const filteredMedications = medications.filter((med) => {
    const isNotSpecified =
      med.medication && med.medication.trim().toLowerCase() === "medication not specified";
    const hasOtherDetails =
      med.form ||
      med.dosage ||
      med.frequency ||
      med.duration ||
      med.xDays ||
      med.prn ||
      med.specialInstructions;
    return !isNotSpecified || hasOtherDetails;
  });

  // Filter out therapies that only have "Therapy not specified" with no other details
  const filteredTherapies = therapies.filter((therapy) => {
    const isNotSpecified =
      therapy.therapy && therapy.therapy.trim().toLowerCase() === "therapy not specified";
    const hasOtherDetails = therapy.reason;
    return !isNotSpecified || hasOtherDetails;
  });

  // Get IV access and fluid volume based on the type
  const ivAccessCode = isNewbornMedsTherapies
    ? "newborn-meds-and-therapies-iv-access"
    : "client-meds-therapies-iv-access";
  const fluidVolumeCode = isNewbornMedsTherapies
    ? "newborn-meds-and-therapies-fluid-volume"
    : "client-meds-therapies-fluid-volume";

  const ivAccess = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === ivAccessCode,
  )?.valueDateTime;

  const fluidVolume = observation?.component
    ?.find((comp) => getCodeableConceptCode(comp.code) === fluidVolumeCode)
    ?.valueQuantity?.value?.toString();

  const notes = getNotesDisplay(observation);
  const hasAnswers = Boolean(
    filteredMedications.length > 0 || filteredTherapies.length > 0 || ivAccess || fluidVolume,
  );
  const hasNotes = !isEmpty(notes);

  return {
    medications: filteredMedications,
    therapies: filteredTherapies,
    ivAccess,
    fluidVolume,
    notes,
    hasAnswers,
    hasNotes,
  };
}

function getInputDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueString(observation, ClientInputOutputComponent.Input);
}

function getOutputDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueString(observation, ClientInputOutputComponent.Output);
}

export function getInputOutputRowValues(observation: Observation): InputOutputRowValues {
  const answers = {
    input: getInputDisplay(observation),
    output: getOutputDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, [CommonComponent.Notes]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

function getStatusDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientPerineumComponent.Status)?.coding?.[0]
    ?.display;
}

function getOtherLacerationDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientPerineumComponent.OtherLaceration)
    ?.coding?.[0]?.display;
}

function getRepairDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientPerineumComponent.Repair)?.coding?.[0]
    ?.display;
}

export function getPerineumRowValues(observation: Observation): PerineumRowValues {
  const answers = {
    status: getStatusDisplay(observation),
    otherLaceration: getOtherLacerationDisplay(observation),
    repair: getRepairDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

function getAlertDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientInitialIppAssessmentComponent.Alert)
    ?.coding?.[0]?.display;
}

function getManagementDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(
    observation,
    ClientInitialIppAssessmentComponent.Management,
  )?.coding?.[0]?.display;
}

function getBleedingDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, ClientInitialIppAssessmentComponent.Bleeding)
    ?.coding?.[0]?.display;
}

export function getInitialIppAssessmentRowValues(
  observation: Observation | undefined,
): InitialIppAssessmentRowValues {
  const answers = {
    alert: getAlertDisplay(observation),
    management: getManagementDisplay(observation),
    bleeding: getBleedingDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

function getNursingDisplay(observation: Observation | undefined): string | undefined {
  const clientNursing = getComponentValueCodeableConcept(
    observation,
    ClientFirstLatchComponent.Nursing,
  )?.coding?.[0]?.display;
  const newbornNursing = getComponentValueCodeableConcept(
    observation,
    NewbornFirstLatchComponent.Nursing,
  )?.coding?.[0]?.display;
  return clientNursing || newbornNursing;
}

function getFormulaDisplay(observation: Observation | undefined): string | undefined {
  const clientFormula = getComponentValueCodeableConcept(
    observation,
    ClientFirstLatchComponent.Formula,
  )?.coding?.[0]?.display;
  const newbornFormula = getComponentValueCodeableConcept(
    observation,
    NewbornFirstLatchComponent.Formula,
  )?.coding?.[0]?.display;
  return clientFormula || newbornFormula;
}

export function getFirstLatchRowValues(observation: Observation | undefined): FirstLatchRowValues {
  const answers = {
    nursing: getNursingDisplay(observation),
    formula: getFormulaDisplay(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, ["notes"]);
  const hasNotes = !isEmpty(answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

export function getCordClampedRowValues(
  observation: Observation | undefined,
): CordClampedRowValues {
  const notes = getNotesDisplay(observation);
  const hasNotes = !isEmpty(notes);
  return { hasNotes, notes, hasAnswers: false };
}

export function getAssessmentNoteRowValues(
  observation: Observation | undefined,
): AssessmentNoteRowValues {
  const notes = getNotesDisplay(observation);
  const hasNotes = !isEmpty(notes);
  return { hasNotes, notes, hasAnswers: false };
}

function getFeedingTypeDisplay(observation: Observation | undefined): string[] {
  const components = observation?.component?.filter((comp) => {
    const code = getCodeableConceptCode(comp.code);
    return code === "newborn-feeding-type";
  });

  return components?.map((comp) => comp.valueCodeableConcept?.coding?.[0]?.display || "") || [];
}

export function getNewbornFeedingRowValues(
  observation: Observation | undefined,
): NewbornFeedingRowValues {
  const feedingTypes = getFeedingTypeDisplay(observation);
  const notes = getNotesDisplay(observation);
  const hasAnswers = feedingTypes.length > 0;
  const hasNotes = !isEmpty(notes);
  return { feedingTypes, notes, hasAnswers, hasNotes };
}

function getNewbornHeartRateDisplay(observation: Observation | undefined): string | undefined {
  const heartRate = getComponentValueQuantity(observation, NewbornVitalsComponent.HeartRate)?.value;
  return heartRate !== undefined ? `${heartRate} bpm` : undefined;
}

function getNewbornRespiratoryRateDisplay(
  observation: Observation | undefined,
): string | undefined {
  const respiratoryRate = getComponentValueQuantity(
    observation,
    NewbornVitalsComponent.RespiratoryRate,
  )?.value;
  return respiratoryRate !== undefined ? `${respiratoryRate} bpm` : undefined;
}

function getNewbornTemperatureDisplay(observation: Observation | undefined): string | undefined {
  const temperatureComponent = getComponentValueQuantity(
    observation,
    NewbornVitalsComponent.Temperature,
  );

  if (!temperatureComponent) return;

  const temperature = temperatureComponent.value;
  if (temperature === undefined) return;

  const temperatureUnitCode = temperatureComponent.code ?? FORM_OPTIONS.Fahrenheit;
  const temperatureUnit =
    TEMPERATURE_UNIT_DISPLAY[temperatureUnitCode as keyof typeof TEMPERATURE_UNIT_DISPLAY];

  const temperatureMethod = getComponentValueCodeableConcept(
    observation,
    NewbornVitalsComponent.TemperatureMethod,
  )?.coding?.[0]?.display;
  const temperatureMethodOther = getComponentValueString(
    observation,
    NewbornVitalsComponent.TemperatureMethodOther,
  );
  if (!temperatureMethod) return `${temperature}°${temperatureUnit}`;

  const method = temperatureMethodOther ? temperatureMethodOther : temperatureMethod;

  const display = `${temperature}°${temperatureUnit} (${method})`;
  return display;
}

function getNewbornColorDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, NewbornVitalsComponent.Color)?.coding?.[0]
    ?.display;
}

function getNewbornVoidDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, NewbornVitalsComponent.Void)?.coding?.[0]
    ?.display;
}

function getNewbornStoolDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueCodeableConcept(observation, NewbornVitalsComponent.Stool)?.coding?.[0]
    ?.display;
}

function getNewbornStoolNotes(observation: Observation | undefined): string | undefined {
  return getComponentValueString(observation, NewbornVitalsComponent.StoolNotes);
}

function getNewbornVoidNotes(observation: Observation | undefined): string | undefined {
  return getComponentValueString(observation, NewbornVitalsComponent.VoidNotes);
}

export function getNewbornVitalsRowValues(observation: Observation | undefined) {
  const answers = {
    heartRate: getNewbornHeartRateDisplay(observation),
    respiratoryRate: getNewbornRespiratoryRateDisplay(observation),
    temperature: getNewbornTemperatureDisplay(observation),
    color: getNewbornColorDisplay(observation),
    void_: getNewbornVoidDisplay(observation),
    stool: getNewbornStoolDisplay(observation),
    stoolNotes: getNewbornStoolNotes(observation),
    voidNotes: getNewbornVoidNotes(observation),
    notes: getNotesDisplay(observation),
  };
  const hasAnswers = hasRecordsAnswers(answers, [CommonComponent.Notes, "stoolNotes", "voidNotes"]);
  const hasNotes = Boolean(answers.stoolNotes || answers.voidNotes || answers.notes);
  return { ...answers, hasAnswers, hasNotes };
}

export function getNewbornLocationRowValues(observation: Observation) {
  const location = observation.component?.find(
    (component) => component.code?.coding?.[0]?.code === "newborn-location",
  )?.valueString;

  const inWater = observation.component?.find(
    (component) => component.code?.coding?.[0]?.code === "newborn-in-water",
  )?.valueCodeableConcept?.coding?.[0]?.display;

  const notes = observation.note?.[0]?.text;

  const hasAnswers = Boolean(location || inWater);
  const hasNotes = Boolean(notes);

  return {
    location,
    inWater,
    notes,
    hasAnswers,
    hasNotes,
  };
}

function getAssessmentTypeDisplay(observation: Observation | undefined): string[] {
  return (
    observation?.component
      ?.map((comp) => comp.valueCodeableConcept?.coding?.[0]?.display)
      .filter((display): display is string => display !== undefined) ?? []
  );
}

export function getNewbornInitialAssessmentRowValues(
  observation: Observation | undefined,
): NewbornInitialAssessmentRowValues {
  const assessmentTypes = getAssessmentTypeDisplay(observation);
  const notes = getNotesDisplay(observation);
  const hasAnswers = assessmentTypes.length > 0;
  const hasNotes = !isEmpty(notes);
  return { assessmentTypes, notes, hasAnswers, hasNotes };
}

function getTypeDisplay(
  observation: Observation | undefined,
): { display: string; valueInteger?: number } | undefined {
  const component = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === NewbornApgarComponent.Type,
  );
  return component?.valueCodeableConcept?.coding?.[0]?.display
    ? {
        display: component.valueCodeableConcept.coding[0].display,
        valueInteger: component.valueInteger,
      }
    : undefined;
}

function getTypeOtherDisplay(observation: Observation | undefined): string | undefined {
  return getComponentValueString(observation, NewbornApgarComponent.TypeOther);
}

function getHeartRateDisplay(
  observation: Observation | undefined,
): { display: string; valueInteger?: number } | undefined {
  const component = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === NewbornApgarComponent.HeartRate,
  );
  return component?.valueCodeableConcept?.coding?.[0]?.display
    ? {
        display: component.valueCodeableConcept.coding[0].display,
        valueInteger: component.valueInteger,
      }
    : undefined;
}

function getRespiratoryEffortDisplay(
  observation: Observation | undefined,
): { display: string; valueInteger?: number } | undefined {
  const component = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === NewbornApgarComponent.RespiratoryEffort,
  );
  return component?.valueCodeableConcept?.coding?.[0]?.display
    ? {
        display: component.valueCodeableConcept.coding[0].display,
        valueInteger: component.valueInteger,
      }
    : undefined;
}

function getReflexStimuliDisplay(
  observation: Observation | undefined,
): { display: string; valueInteger?: number } | undefined {
  const component = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === NewbornApgarComponent.ReflexStimuli,
  );
  return component?.valueCodeableConcept?.coding?.[0]?.display
    ? {
        display: component.valueCodeableConcept.coding[0].display,
        valueInteger: component.valueInteger,
      }
    : undefined;
}

function getMuscleToneDisplay(
  observation: Observation | undefined,
): { display: string; valueInteger?: number } | undefined {
  const component = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === NewbornApgarComponent.MuscleTone,
  );
  return component?.valueCodeableConcept?.coding?.[0]?.display
    ? {
        display: component.valueCodeableConcept.coding[0].display,
        valueInteger: component.valueInteger,
      }
    : undefined;
}

function getColorDisplay(
  observation: Observation | undefined,
): { display: string; valueInteger?: number } | undefined {
  const component = observation?.component?.find(
    (comp) => getCodeableConceptCode(comp.code) === NewbornApgarComponent.Color,
  );
  return component?.valueCodeableConcept?.coding?.[0]?.display
    ? {
        display: component.valueCodeableConcept.coding[0].display,
        valueInteger: component.valueInteger,
      }
    : undefined;
}

export function getApgarRowValues(observation: Observation | undefined): ApgarRowValues {
  const type = getTypeDisplay(observation);
  const typeOther = getTypeOtherDisplay(observation);
  const heartRate = getHeartRateDisplay(observation);
  const respiratoryEffort = getRespiratoryEffortDisplay(observation);
  const reflexStimuli = getReflexStimuliDisplay(observation);
  const muscleTone = getMuscleToneDisplay(observation);
  const color = getColorDisplay(observation);
  const notes = getNotesDisplay(observation);
  const totalScore = observation?.valueInteger;

  const answers = {
    type: type?.display,
    typeOther,
    heartRate: heartRate?.display,
    respiratoryEffort: respiratoryEffort?.display,
    reflexStimuli: reflexStimuli?.display,
    muscleTone: muscleTone?.display,
    color: color?.display,
    notes,
    totalScore,
  };

  const hasAnswers = hasRecordsAnswers(answers, [CommonComponent.Notes]);
  const hasNotes = !isEmpty(notes);

  return {
    type,
    typeOther,
    heartRate,
    respiratoryEffort,
    reflexStimuli,
    muscleTone,
    color,
    notes,
    totalScore,
    hasAnswers,
    hasNotes,
  };
}
